import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { floorPlanAPI, projectAPI, inventoryAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, Save, Trash2, Plus, Search, Map, Eye, Edit, 
  ArrowLeft, MousePointer, Square, Compass, Info, 
  CheckCircle, XCircle, AlertCircle, Home, Building2, Copy, Calculator
} from 'lucide-react';

const STATUS_COLORS = {
  available: 'rgba(34, 197, 94, 0.4)',    // Green
  sold: 'rgba(239, 68, 68, 0.4)',          // Red
  blocked: 'rgba(234, 179, 8, 0.4)',       // Yellow
  booked: 'rgba(59, 130, 246, 0.4)',       // Blue
};

const STATUS_BORDERS = {
  available: '#22c55e',
  sold: '#ef4444',
  blocked: '#eab308',
  booked: '#3b82f6',
};

const FACING_OPTIONS = [
  { value: 'north', label: 'North' },
  { value: 'south', label: 'South' },
  { value: 'east', label: 'East' },
  { value: 'west', label: 'West' },
  { value: 'north_east', label: 'North-East' },
  { value: 'north_west', label: 'North-West' },
  { value: 'south_east', label: 'South-East' },
  { value: 'south_west', label: 'South-West' },
];

const FloorPlanMapper = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  const isBuilder = user?.role === 'builder_admin';
  const isEditMode = id !== 'new' && isBuilder;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [floorPlan, setFloorPlan] = useState(null);
  const [projects, setProjects] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  
  // Drawing state
  const [image, setImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [units, setUnits] = useState([]);
  const [currentPolygon, setCurrentPolygon] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingTool, setDrawingTool] = useState('polygon'); // 'polygon' or 'rectangle'
  const [rectStart, setRectStart] = useState(null); // For rectangle drawing
  const [rectEnd, setRectEnd] = useState(null); // For rectangle drawing
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [hoveredUnit, setHoveredUnit] = useState(null);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [showUnitDetailsPopup, setShowUnitDetailsPopup] = useState(false);
  const [showAddUnitDialog, setShowAddUnitDialog] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [unitFormData, setUnitFormData] = useState({
    inventory_unit_id: '',
    unit_number: '',
    door_facing: '',
    notes: ''
  });
  
  // New unit entry form
  const [newUnitData, setNewUnitData] = useState({
    floor: 1,
    unit_number: '',
    bhk_type: '2 BHK',
    carpet_area: '',
    saleable_area: '',
    door_facing: 'north',
    status: 'available'
  });
  
  // Price Calculator state
  const [showPriceCalculator, setShowPriceCalculator] = useState(false);
  const [priceCalcData, setPriceCalcData] = useState({
    basePrice: 8500,
    floorRise: 50,
    selectedFloor: 1
  });
  
  // Form state for new floor plan
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    tower: '',
    floor_numbers: ''
  });

  useEffect(() => {
    loadInitialData();
  }, [id]);

  useEffect(() => {
    if (imageLoaded) {
      drawCanvas();
    }
  }, [imageLoaded, units, currentPolygon, selectedUnit, hoveredUnit, seriesFilter, rectStart, rectEnd]);

  const loadInitialData = async () => {
    try {
      const [projectsRes] = await Promise.all([
        projectAPI.list()
      ]);
      setProjects(projectsRes.data);

      if (id && id !== 'new') {
        const fpRes = await floorPlanAPI.get(id);
        setFloorPlan(fpRes.data);
        setUnits(fpRes.data.units || []);
        setFormData({
          name: fpRes.data.name,
          description: fpRes.data.description || '',
          project_id: fpRes.data.project_id,
          tower: fpRes.data.tower || '',
          floor_numbers: (fpRes.data.floor_numbers || []).join(', ')
        });
        setSelectedProject(fpRes.data.project_id);
        
        // Load image
        if (fpRes.data.image_url) {
          loadImage(fpRes.data.image_url);
        }
        
        // Load inventory for this project
        const invRes = await inventoryAPI.list({ project_id: fpRes.data.project_id });
        setInventory(invRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadImage = async (url) => {
    // For external URLs, fetch and convert to data URL to avoid CORS issues
    try {
      // First try loading directly (works for same-origin images)
      const img = new Image();
      
      // If it's a data URL or same-origin, load directly
      if (url.startsWith('data:') || url.startsWith(window.location.origin)) {
        img.onload = () => {
          setImage(img);
          setImageLoaded(true);
        };
        img.onerror = () => {
          toast.error('Failed to load floor plan image');
        };
        img.src = url;
        return;
      }
      
      // For external URLs, try to fetch and convert to blob
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        
        img.onload = () => {
          setImage(img);
          setImageLoaded(true);
        };
        img.src = dataUrl;
      } catch (fetchError) {
        // If fetch fails, try loading directly (might work with CORS headers)
        img.onload = () => {
          setImage(img);
          setImageLoaded(true);
        };
        img.onerror = () => {
          toast.error('Failed to load floor plan image. Try uploading a local image.');
        };
        img.src = url;
      }
    } catch (error) {
      toast.error('Failed to load floor plan image');
    }
  };

  const handleProjectChange = async (projectId) => {
    setSelectedProject(projectId);
    setFormData(prev => ({ ...prev, project_id: projectId }));
    
    try {
      const invRes = await inventoryAPI.list({ project_id: projectId });
      setInventory(invRes.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setImageLoaded(true);
        setFormData(prev => ({
          ...prev,
          image_url: event.target.result,
          image_width: img.width,
          image_height: img.height
        }));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    
    // Set canvas size based on container
    const maxWidth = container.clientWidth - 40;
    const maxHeight = 600;
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
    
    canvas.width = image.width * scale;
    canvas.height = image.height * scale;
    
    // Clear and draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    
    // Draw existing units
    units.forEach(unit => {
      const shouldHighlight = !seriesFilter || unit.unit_number.endsWith(seriesFilter);
      const isSelected = selectedUnit?.id === unit.id;
      const isHovered = hoveredUnit?.id === unit.id;
      
      if (!shouldHighlight && seriesFilter) {
        // Draw dimmed
        drawPolygon(ctx, unit.polygon, 'rgba(128, 128, 128, 0.2)', '#888', scale, false, false);
      } else {
        const status = unit.status || 'available';
        const fillColor = STATUS_COLORS[status] || STATUS_COLORS.available;
        const strokeColor = STATUS_BORDERS[status] || STATUS_BORDERS.available;
        drawPolygon(ctx, unit.polygon, fillColor, strokeColor, scale, isSelected, isHovered);
        
        // Draw unit label
        if (unit.polygon.length > 0) {
          const center = getPolygonCenter(unit.polygon);
          ctx.fillStyle = '#1f2937';
          ctx.font = 'bold 12px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(unit.unit_number, center.x * scale, center.y * scale);
        }
      }
    });
    
    // Draw current polygon being created
    if (currentPolygon.length > 0) {
      drawPolygon(ctx, currentPolygon, 'rgba(59, 130, 246, 0.3)', '#3b82f6', scale, false, false, true);
      
      // Draw vertices
      currentPolygon.forEach(point => {
        ctx.beginPath();
        ctx.arc(point.x * scale, point.y * scale, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#3b82f6';
        ctx.fill();
      });
    }
    
    // Draw rectangle preview while dragging
    if (rectStart && rectEnd) {
      const rectPoints = [
        { x: rectStart.x, y: rectStart.y },
        { x: rectEnd.x, y: rectStart.y },
        { x: rectEnd.x, y: rectEnd.y },
        { x: rectStart.x, y: rectEnd.y }
      ];
      drawPolygon(ctx, rectPoints, 'rgba(59, 130, 246, 0.3)', '#3b82f6', scale, false, false, true);
    }
  }, [image, units, currentPolygon, selectedUnit, hoveredUnit, seriesFilter, rectStart, rectEnd]);

  const drawPolygon = (ctx, points, fillColor, strokeColor, scale, isSelected, isHovered, isDashed = false) => {
    if (points.length < 2) return;
    
    ctx.beginPath();
    ctx.moveTo(points[0].x * scale, points[0].y * scale);
    points.forEach((point, i) => {
      if (i > 0) ctx.lineTo(point.x * scale, point.y * scale);
    });
    ctx.closePath();
    
    ctx.fillStyle = fillColor;
    ctx.fill();
    
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = isSelected ? 3 : isHovered ? 2.5 : 2;
    if (isDashed) ctx.setLineDash([5, 5]);
    else ctx.setLineDash([]);
    ctx.stroke();
    
    if (isSelected) {
      ctx.strokeStyle = '#1f2937';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
    }
  };

  const getPolygonCenter = (points) => {
    const x = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const y = points.reduce((sum, p) => sum + p.y, 0) / points.length;
    return { x, y };
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;
    
    // Convert back to image coordinates
    const imageScale = canvas.width / image.width;
    return {
      x: canvasX / imageScale,
      y: canvasY / imageScale
    };
  };

  const isPointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      if (((yi > point.y) !== (yj > point.y)) &&
          (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  };

  const handleCanvasMouseDown = (e) => {
    if (!isBuilder || !isDrawing) return;
    
    if (drawingTool === 'rectangle') {
      const coords = getCanvasCoordinates(e);
      setRectStart(coords);
      setRectEnd(coords);
    }
  };

  const handleCanvasMouseUp = (e) => {
    if (!isBuilder || !isDrawing) return;
    
    if (drawingTool === 'rectangle' && rectStart) {
      const coords = getCanvasCoordinates(e);
      // Create rectangle polygon from start and end points
      const rectPolygon = [
        { x: rectStart.x, y: rectStart.y },
        { x: coords.x, y: rectStart.y },
        { x: coords.x, y: coords.y },
        { x: rectStart.x, y: coords.y }
      ];
      
      // Check if rectangle is big enough (at least 20x20 pixels)
      const width = Math.abs(coords.x - rectStart.x);
      const height = Math.abs(coords.y - rectStart.y);
      
      if (width > 20 && height > 20) {
        setCurrentPolygon(rectPolygon);
        setShowUnitDialog(true);
      } else {
        toast.error('Rectangle too small. Please draw a larger area.');
      }
      
      setRectStart(null);
      setRectEnd(null);
    }
  };

  const handleCanvasClick = (e) => {
    if (!isBuilder || !isDrawing) {
      // View mode - check if clicked on a unit
      const coords = getCanvasCoordinates(e);
      const clickedUnit = units.find(u => isPointInPolygon(coords, u.polygon));
      if (clickedUnit) {
        setSelectedUnit(clickedUnit);
      } else {
        setSelectedUnit(null);
      }
      return;
    }
    
    // Polygon drawing mode (not for rectangle)
    if (drawingTool === 'polygon') {
      const coords = getCanvasCoordinates(e);
      setCurrentPolygon(prev => [...prev, coords]);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!image) return;
    
    const coords = getCanvasCoordinates(e);
    
    // Update rectangle preview while dragging
    if (isDrawing && drawingTool === 'rectangle' && rectStart) {
      setRectEnd(coords);
    }
    
    const hovered = units.find(u => isPointInPolygon(coords, u.polygon));
    setHoveredUnit(hovered || null);
  };

  const finishPolygon = () => {
    if (currentPolygon.length < 3) {
      toast.error('A polygon needs at least 3 points');
      return;
    }
    
    setShowUnitDialog(true);
  };

  const cancelPolygon = () => {
    setCurrentPolygon([]);
    setRectStart(null);
    setRectEnd(null);
    setIsDrawing(false);
  };

  const handleSaveUnit = async () => {
    if (!unitFormData.inventory_unit_id) {
      toast.error('Please select a unit from inventory');
      return;
    }
    
    const selectedInvUnit = inventory.find(u => u.id === unitFormData.inventory_unit_id);
    
    const newUnit = {
      id: `unit-${Date.now()}`,
      inventory_unit_id: unitFormData.inventory_unit_id,
      unit_number: selectedInvUnit?.unit_number || unitFormData.unit_number,
      polygon: currentPolygon,
      door_facing: unitFormData.door_facing,
      notes: unitFormData.notes,
      status: selectedInvUnit?.status || 'available'
    };
    
    setUnits(prev => [...prev, newUnit]);
    setCurrentPolygon([]);
    setIsDrawing(false);
    setShowUnitDialog(false);
    setUnitFormData({ inventory_unit_id: '', unit_number: '', door_facing: '', notes: '' });
    
    toast.success('Unit mapped successfully');
  };

  // Create new inventory unit and map it
  const handleCreateAndMapUnit = async () => {
    if (!newUnitData.unit_number || !newUnitData.carpet_area) {
      toast.error('Please fill in Flat No and Carpet Area');
      return;
    }
    
    try {
      // Create the inventory unit
      const invData = {
        project_id: formData.project_id,
        unit_number: newUnitData.unit_number,
        floor: Number(newUnitData.floor),
        bhk_type: newUnitData.bhk_type,
        carpet_area: Number(newUnitData.carpet_area),
        saleable_area: Number(newUnitData.saleable_area) || Math.round(Number(newUnitData.carpet_area) * 1.25),
        door_facing: newUnitData.door_facing,
        status: newUnitData.status
      };
      
      const response = await inventoryAPI.create(invData);
      const createdUnit = response.data;
      
      // Add to local inventory list
      setInventory(prev => [...prev, createdUnit]);
      
      // Map the new unit to the floor plan
      const newUnit = {
        id: `unit-${Date.now()}`,
        inventory_unit_id: createdUnit.id,
        unit_number: createdUnit.unit_number,
        polygon: currentPolygon,
        door_facing: createdUnit.door_facing,
        status: createdUnit.status,
        carpet_area: createdUnit.carpet_area,
        saleable_area: createdUnit.saleable_area,
        bhk_type: createdUnit.bhk_type,
        floor: createdUnit.floor
      };
      
      setUnits(prev => [...prev, newUnit]);
      setCurrentPolygon([]);
      setIsDrawing(false);
      setShowAddUnitDialog(false);
      setNewUnitData({
        floor: 1,
        unit_number: '',
        bhk_type: '2 BHK',
        carpet_area: '',
        saleable_area: '',
        door_facing: 'north',
        status: 'available'
      });
      
      toast.success(`Unit ${createdUnit.unit_number} created and mapped successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create unit');
    }
  };

  const handleDeleteUnit = (unitId) => {
    setUnits(prev => prev.filter(u => u.id !== unitId));
    setSelectedUnit(null);
    toast.success('Unit removed');
  };

  const handleSaveFloorPlan = async () => {
    if (!formData.name || !formData.project_id) {
      toast.error('Please fill in name and select a project');
      return;
    }
    
    setSaving(true);
    try {
      const floorPlanData = {
        name: formData.name,
        description: formData.description,
        project_id: formData.project_id,
        tower: formData.tower,
        floor_numbers: formData.floor_numbers.split(',').map(f => f.trim()).filter(Boolean),
        image_url: formData.image_url || floorPlan?.image_url,
        image_width: formData.image_width || image?.width || 0,
        image_height: formData.image_height || image?.height || 0,
        units: units.map(u => ({
          id: u.id,
          inventory_unit_id: u.inventory_unit_id,
          unit_number: u.unit_number,
          polygon: u.polygon,
          door_facing: u.door_facing,
          notes: u.notes
        }))
      };
      
      if (id && id !== 'new') {
        await floorPlanAPI.update(id, floorPlanData);
        toast.success('Floor plan updated');
      } else {
        const res = await floorPlanAPI.create(floorPlanData);
        toast.success('Floor plan created');
        navigate(`/builder/floor-plans/${res.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save floor plan');
    } finally {
      setSaving(false);
    }
  };

  // Stats
  const stats = units.reduce((acc, u) => {
    const status = u.status || 'available';
    acc[status] = (acc[status] || 0) + 1;
    acc.total++;
    return acc;
  }, { total: 0 });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="floor-plan-mapper">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Map className="h-6 w-6 text-blue-600" />
              {id === 'new' ? 'Create Floor Plan' : floorPlan?.name || 'Floor Plan'}
            </h1>
            <p className="text-muted-foreground">
              {isBuilder ? 'Map units on the floor plan' : 'View unit availability'}
            </p>
          </div>
        </div>
        
        {isBuilder && (
          <div className="flex gap-2">
            <Button onClick={handleSaveFloorPlan} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Floor Plan'}
            </Button>
            {units.length > 0 && (
              <Button 
                variant="outline" 
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all unit mappings? This cannot be undone.')) {
                    setUnits([]);
                    setSelectedUnit(null);
                    toast.success('All unit mappings cleared');
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All Mappings
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Stats Bar */}
      {units.length > 0 && (
        <div className="flex gap-4">
          <Badge variant="outline" className="px-3 py-1">
            Total: {stats.total}
          </Badge>
          <Badge className="bg-green-100 text-green-800 px-3 py-1">
            <CheckCircle className="h-3 w-3 mr-1" />
            Available: {stats.available || 0}
          </Badge>
          <Badge className="bg-red-100 text-red-800 px-3 py-1">
            <XCircle className="h-3 w-3 mr-1" />
            Sold: {stats.sold || 0}
          </Badge>
          <Badge className="bg-yellow-100 text-yellow-800 px-3 py-1">
            <AlertCircle className="h-3 w-3 mr-1" />
            Blocked: {stats.blocked || 0}
          </Badge>
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Left Panel - Settings & Controls */}
        <div className="space-y-4">
          {/* Floor Plan Settings */}
          {isBuilder && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Floor Plan Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Tower A - Typical Floor"
                  />
                </div>
                <div>
                  <Label className="text-xs">Project *</Label>
                  <Select value={formData.project_id} onValueChange={handleProjectChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tower</Label>
                  <Input
                    value={formData.tower}
                    onChange={(e) => setFormData(prev => ({ ...prev, tower: e.target.value }))}
                    placeholder="Tower A"
                  />
                </div>
                <div>
                  <Label className="text-xs">Floors (comma-separated)</Label>
                  <Input
                    value={formData.floor_numbers}
                    onChange={(e) => setFormData(prev => ({ ...prev, floor_numbers: e.target.value }))}
                    placeholder="2, 3, 4, 5"
                  />
                </div>
                
                {/* Image Upload */}
                <div>
                  <Label className="text-xs">Floor Plan Image</Label>
                  <label className="flex items-center justify-center p-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                    <div className="text-center">
                      <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">
                        {image ? 'Change Image' : 'Upload Image'}
                      </span>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Drawing Tools */}
          {isBuilder && image && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Drawing Tools</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!isDrawing ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground mb-2">Select a tool to mark unit boundaries:</p>
                    <Button 
                      className="w-full justify-start" 
                      onClick={() => { setDrawingTool('rectangle'); setIsDrawing(true); }}
                      variant="outline"
                    >
                      <Square className="h-4 w-4 mr-2" />
                      Rectangle Tool
                      <span className="ml-auto text-xs text-muted-foreground">Click & Drag</span>
                    </Button>
                    <Button 
                      className="w-full justify-start" 
                      onClick={() => { setDrawingTool('polygon'); setIsDrawing(true); }}
                      variant="outline"
                    >
                      <MousePointer className="h-4 w-4 mr-2" />
                      Polygon Tool
                      <span className="ml-auto text-xs text-muted-foreground">Click Corners</span>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                      {drawingTool === 'rectangle' ? (
                        <Square className="h-4 w-4 text-blue-600" />
                      ) : (
                        <MousePointer className="h-4 w-4 text-blue-600" />
                      )}
                      <span className="text-sm font-medium text-blue-700">
                        {drawingTool === 'rectangle' ? 'Rectangle Tool' : 'Polygon Tool'}
                      </span>
                    </div>
                    
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        {drawingTool === 'rectangle' 
                          ? 'Click and drag on the floor plan to draw a rectangle over the flat.'
                          : 'Click on corners of the flat to draw a polygon. Minimum 3 points required.'}
                      </AlertDescription>
                    </Alert>
                    
                    {drawingTool === 'polygon' && (
                      <>
                        <p className="text-xs text-center font-medium">Points: {currentPolygon.length}</p>
                        <Button 
                          className="w-full" 
                          onClick={finishPolygon}
                          disabled={currentPolygon.length < 3}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Done ({currentPolygon.length}/3 min)
                        </Button>
                      </>
                    )}
                    
                    <Button 
                      variant="outline" 
                      onClick={cancelPolygon}
                      className="w-full"
                    >
                      Cancel Drawing
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Series Filter */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Filter Units</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Series Filter</Label>
                <Input
                  value={seriesFilter}
                  onChange={(e) => setSeriesFilter(e.target.value)}
                  placeholder="e.g., 01 for all x01 units"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Shows only units ending with this suffix
                </p>
              </div>
              {seriesFilter && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSeriesFilter('')}
                  className="w-full"
                >
                  Clear Filter
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Selected Unit Info */}
          {selectedUnit && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Flat {selectedUnit.unit_number}
                </CardTitle>
                <Badge className={
                  selectedUnit.status === 'sold' ? 'bg-red-500' :
                  selectedUnit.status === 'blocked' ? 'bg-yellow-500' :
                  'bg-green-500'
                }>
                  {selectedUnit.status || 'Available'}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {/* Size Information */}
                <div className="p-2 bg-white rounded-lg space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Size</p>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Carpet Area</span>
                    <span className="font-medium">{selectedUnit.carpet_area || '-'} sq.ft</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saleable Area</span>
                    <span className="font-medium">{selectedUnit.saleable_area || '-'} sq.ft</span>
                  </div>
                </div>
                
                {/* Unit Details */}
                <div className="p-2 bg-white rounded-lg space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Details</p>
                  {selectedUnit.bhk_type && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium">{selectedUnit.bhk_type}</span>
                    </div>
                  )}
                  {selectedUnit.floor && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Floor</span>
                      <span className="font-medium">{selectedUnit.floor}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Door Facing</span>
                    <span className="font-medium capitalize">
                      {selectedUnit.door_facing ? selectedUnit.door_facing.replace('_', '-') : '-'}
                    </span>
                  </div>
                </div>
                
                {/* Price Information */}
                <div className="p-2 bg-white rounded-lg space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Price</p>
                  {selectedUnit.base_price && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Base Rate</span>
                      <span className="font-medium">₹{selectedUnit.base_price?.toLocaleString()}/sq.ft</span>
                    </div>
                  )}
                  {selectedUnit.floor_rise && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Floor Rise</span>
                      <span className="font-medium">₹{selectedUnit.floor_rise?.toLocaleString()}/floor</span>
                    </div>
                  )}
                  {selectedUnit.price && (
                    <div className="flex justify-between pt-1 border-t">
                      <span className="font-semibold">Total Price</span>
                      <span className="font-bold text-green-600">
                        ₹{(selectedUnit.price / 100000).toFixed(2)} L
                      </span>
                    </div>
                  )}
                </div>
                
                {isBuilder && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="w-full mt-2"
                    onClick={() => handleDeleteUnit(selectedUnit.id)}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Remove Mapping
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Legend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Legend</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: STATUS_COLORS.available, border: `2px solid ${STATUS_BORDERS.available}` }} />
                <span className="text-sm">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: STATUS_COLORS.sold, border: `2px solid ${STATUS_BORDERS.sold}` }} />
                <span className="text-sm">Sold</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: STATUS_COLORS.blocked, border: `2px solid ${STATUS_BORDERS.blocked}` }} />
                <span className="text-sm">Blocked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: STATUS_COLORS.booked, border: `2px solid ${STATUS_BORDERS.booked}` }} />
                <span className="text-sm">Booked</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Canvas Area */}
        <div className="lg:col-span-3" ref={containerRef}>
          <Card className="overflow-hidden">
            <CardContent className="p-4">
              {!image ? (
                <div className="flex flex-col items-center justify-center h-96 bg-muted/30 rounded-lg border-2 border-dashed">
                  <Building2 className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    {isBuilder ? 'Upload a floor plan image to get started' : 'No floor plan image available'}
                  </p>
                </div>
              ) : (
                <div className="relative ph-no-capture">
                  <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseUp={handleCanvasMouseUp}
                    onMouseMove={handleCanvasMouseMove}
                    onMouseLeave={() => { setRectEnd(null); }}
                    className={`max-w-full ph-no-capture ${isDrawing ? 'cursor-crosshair' : 'cursor-pointer'}`}
                    style={{ display: 'block', margin: '0 auto' }}
                    data-ph-capture-attribute-ignore="true"
                    data-recording-sensitive="true"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* All Units Summary Table */}
      {units.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Floor Units Summary</CardTitle>
              <Badge variant="outline">{units.length} Units Mapped</Badge>
            </div>
            <CardDescription>All flats on this floor plan with their details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-semibold">Flat No</th>
                    <th className="text-left p-3 font-semibold">Type</th>
                    <th className="text-left p-3 font-semibold">Floor</th>
                    <th className="text-right p-3 font-semibold">Carpet (sq.ft)</th>
                    <th className="text-right p-3 font-semibold">Saleable (sq.ft)</th>
                    <th className="text-left p-3 font-semibold">Facing</th>
                    <th className="text-right p-3 font-semibold">Price</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((unit, index) => (
                    <tr 
                      key={unit.id} 
                      className={`border-b hover:bg-muted/30 cursor-pointer ${selectedUnit?.id === unit.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedUnit(unit)}
                    >
                      <td className="p-3 font-medium">{unit.unit_number}</td>
                      <td className="p-3">{unit.bhk_type || '-'}</td>
                      <td className="p-3">{unit.floor || '-'}</td>
                      <td className="p-3 text-right">{unit.carpet_area?.toLocaleString() || '-'}</td>
                      <td className="p-3 text-right">{unit.saleable_area?.toLocaleString() || '-'}</td>
                      <td className="p-3 capitalize">{unit.door_facing?.replace('_', '-') || '-'}</td>
                      <td className="p-3 text-right font-medium">
                        {unit.price ? `₹${(unit.price / 100000).toFixed(2)}L` : '-'}
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={
                          unit.status === 'sold' ? 'bg-red-500' :
                          unit.status === 'blocked' ? 'bg-yellow-500' :
                          'bg-green-500'
                        }>
                          {unit.status || 'Available'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Price Calculator Info */}
            {units.some(u => u.base_price) && (
              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Price Calculation Formula
                </p>
                <p className="text-xs text-muted-foreground">
                  Total Price = Saleable Area × (Base Rate + Floor Rise × (Floor - 1))
                </p>
                <div className="mt-2 flex gap-4 text-xs">
                  <span>Base Rate: ₹{units[0]?.base_price?.toLocaleString()}/sq.ft</span>
                  <span>Floor Rise: ₹{units[0]?.floor_rise?.toLocaleString()}/floor</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => setShowPriceCalculator(true)}
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Open Price Calculator
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Price Calculator Dialog */}
      <Dialog open={showPriceCalculator} onOpenChange={setShowPriceCalculator}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Price Calculator - Floor Wise Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Base Price (₹/sq.ft)</Label>
                <Input
                  type="number"
                  value={priceCalcData.basePrice}
                  onChange={(e) => setPriceCalcData(prev => ({ ...prev, basePrice: Number(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Floor Rise (₹/sq.ft/floor)</Label>
                <Input
                  type="number"
                  value={priceCalcData.floorRise}
                  onChange={(e) => setPriceCalcData(prev => ({ ...prev, floorRise: Number(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Select Floor</Label>
                <Select 
                  value={String(priceCalcData.selectedFloor)} 
                  onValueChange={(v) => setPriceCalcData(prev => ({ ...prev, selectedFloor: Number(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select floor" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30].map(f => (
                      <SelectItem key={f} value={String(f)}>Floor {f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Price Rate Display */}
            <div className="p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rate for Floor {priceCalcData.selectedFloor}</p>
                <p className="text-xl font-bold text-blue-600">
                  ₹{(priceCalcData.basePrice + (priceCalcData.selectedFloor - 1) * priceCalcData.floorRise).toLocaleString()}/sq.ft
                </p>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <p>Base: ₹{priceCalcData.basePrice.toLocaleString()}</p>
                <p>+ Rise: ₹{((priceCalcData.selectedFloor - 1) * priceCalcData.floorRise).toLocaleString()}</p>
              </div>
            </div>
            
            {/* All Flats on Selected Floor */}
            <div>
              <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Home className="h-4 w-4" />
                All Flats on Floor {priceCalcData.selectedFloor}
              </p>
              
              {(() => {
                const floorUnits = inventory.filter(u => u.floor === priceCalcData.selectedFloor);
                const pricePerSqft = priceCalcData.basePrice + (priceCalcData.selectedFloor - 1) * priceCalcData.floorRise;
                
                if (floorUnits.length === 0) {
                  return (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        No units found for Floor {priceCalcData.selectedFloor}. Add units in Inventory module.
                      </AlertDescription>
                    </Alert>
                  );
                }
                
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-muted">
                          <th className="text-left p-3 font-semibold">Flat No</th>
                          <th className="text-left p-3 font-semibold">Type</th>
                          <th className="text-right p-3 font-semibold">Carpet (sq.ft)</th>
                          <th className="text-right p-3 font-semibold">Saleable (sq.ft)</th>
                          <th className="text-left p-3 font-semibold">Facing</th>
                          <th className="text-right p-3 font-semibold">Calculated Price</th>
                          <th className="text-center p-3 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {floorUnits.map((unit, index) => {
                          const saleableArea = unit.saleable_area || (unit.carpet_area * 1.25);
                          const calculatedPrice = saleableArea * pricePerSqft;
                          return (
                            <tr key={unit.id} className={index % 2 === 0 ? 'bg-white' : 'bg-muted/30'}>
                              <td className="p-3 font-medium">{unit.unit_number}</td>
                              <td className="p-3">{unit.bhk_type || '-'}</td>
                              <td className="p-3 text-right">{unit.carpet_area?.toLocaleString() || '-'}</td>
                              <td className="p-3 text-right">{saleableArea?.toLocaleString() || '-'}</td>
                              <td className="p-3 capitalize">{unit.door_facing?.replace('_', '-') || '-'}</td>
                              <td className="p-3 text-right font-semibold text-green-600">
                                ₹{(calculatedPrice / 100000).toFixed(2)}L
                              </td>
                              <td className="p-3 text-center">
                                <Badge className={
                                  unit.status === 'sold' ? 'bg-red-500' :
                                  unit.status === 'blocked' ? 'bg-yellow-500' :
                                  'bg-green-500'
                                }>
                                  {unit.status || 'available'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted font-semibold">
                          <td colSpan={5} className="p-3 text-right">Floor {priceCalcData.selectedFloor} Total:</td>
                          <td className="p-3 text-right text-green-600">
                            ₹{(floorUnits.reduce((sum, u) => {
                              const saleableArea = u.saleable_area || (u.carpet_area * 1.25);
                              return sum + (saleableArea * pricePerSqft);
                            }, 0) / 10000000).toFixed(2)} Cr
                          </td>
                          <td className="p-3 text-center">
                            {floorUnits.filter(u => u.status === 'available').length} Available
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()}
            </div>
            
            {/* Formula Info */}
            <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
              <p className="font-medium mb-1">Price Calculation Formula:</p>
              <p>Total Price = Saleable Area × (Base Price + Floor Rise × (Floor - 1))</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceCalculator(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unit Mapping Dialog - With Tabs for Select or Add New */}
      <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Map Unit to Floor Plan</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="select" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="select">Select from Inventory</TabsTrigger>
              <TabsTrigger value="add">Add New Unit</TabsTrigger>
            </TabsList>
            
            {/* Tab 1: Select from Inventory */}
            <TabsContent value="select" className="space-y-4 pt-4">
              <div>
                <Label>Select Unit from Inventory *</Label>
                {inventory.length === 0 ? (
                  <Alert className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No inventory units found. Use "Add New Unit" tab to create one.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">
                      {inventory.filter(u => !units.find(mapped => mapped.inventory_unit_id === u.id)).length} units available to map
                    </p>
                    <Select 
                      value={unitFormData.inventory_unit_id} 
                      onValueChange={(v) => setUnitFormData(prev => ({ ...prev, inventory_unit_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {inventory
                          .filter(u => !units.find(mapped => mapped.inventory_unit_id === u.id))
                          .map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.unit_number} - {u.bhk_type} ({u.status})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
              
              <div>
                <Label>Door Facing</Label>
                <Select 
                  value={unitFormData.door_facing} 
                  onValueChange={(v) => setUnitFormData(prev => ({ ...prev, door_facing: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select facing direction" />
                  </SelectTrigger>
                  <SelectContent>
                    {FACING_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={unitFormData.notes}
                  onChange={(e) => setUnitFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes"
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowUnitDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveUnit}>
                  Map Unit
                </Button>
              </DialogFooter>
            </TabsContent>
            
            {/* Tab 2: Add New Unit */}
            <TabsContent value="add" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Floor *</Label>
                  <Select 
                    value={String(newUnitData.floor)} 
                    onValueChange={(v) => setNewUnitData(prev => ({ ...prev, floor: Number(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,20,25,30].map(f => (
                        <SelectItem key={f} value={String(f)}>Floor {f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Flat No *</Label>
                  <Input
                    value={newUnitData.unit_number}
                    onChange={(e) => setNewUnitData(prev => ({ ...prev, unit_number: e.target.value }))}
                    placeholder="e.g., 101, 201"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Carpet Area (sq.ft) *</Label>
                  <Input
                    type="number"
                    value={newUnitData.carpet_area}
                    onChange={(e) => setNewUnitData(prev => ({ ...prev, carpet_area: e.target.value }))}
                    placeholder="e.g., 1000"
                  />
                </div>
                <div>
                  <Label>Saleable Area (sq.ft)</Label>
                  <Input
                    type="number"
                    value={newUnitData.saleable_area}
                    onChange={(e) => setNewUnitData(prev => ({ ...prev, saleable_area: e.target.value }))}
                    placeholder="Auto: Carpet × 1.25"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select 
                    value={newUnitData.bhk_type} 
                    onValueChange={(v) => setNewUnitData(prev => ({ ...prev, bhk_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 BHK">1 BHK</SelectItem>
                      <SelectItem value="1.5 BHK">1.5 BHK</SelectItem>
                      <SelectItem value="2 BHK">2 BHK</SelectItem>
                      <SelectItem value="2.5 BHK">2.5 BHK</SelectItem>
                      <SelectItem value="3 BHK">3 BHK</SelectItem>
                      <SelectItem value="3.5 BHK">3.5 BHK</SelectItem>
                      <SelectItem value="4 BHK">4 BHK</SelectItem>
                      <SelectItem value="4+ BHK">4+ BHK</SelectItem>
                      <SelectItem value="Studio">Studio</SelectItem>
                      <SelectItem value="Penthouse">Penthouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Door Facing *</Label>
                  <Select 
                    value={newUnitData.door_facing} 
                    onValueChange={(v) => setNewUnitData(prev => ({ ...prev, door_facing: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FACING_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Status</Label>
                <Select 
                  value={newUnitData.status} 
                  onValueChange={(v) => setNewUnitData(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  This will create a new unit in Inventory and map it to the floor plan.
                </AlertDescription>
              </Alert>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowUnitDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateAndMapUnit}>
                  <Plus className="h-4 w-4 mr-1" />
                  Create & Map Unit
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FloorPlanMapper;
